import React, { useEffect, useState } from 'react';
import './css/FeaturesSection.css';
import { DynamicIcon } from 'lucide-react/dynamic';
import { Scale } from 'lucide-react';
import axios from 'axios';
import { normalizeServiceIconKey } from '../constants/lucideServiceSectionIcons';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const defaultFeatures = [
  {
    id: '01',
    title: 'Regulatory Strategy',
    iconKey: 'scale',
    desc:
      'We align your litigation posture with broader business objectives to minimise risk and disruption.',
  },
  {
    id: '02',
    title: 'Corporate Advisory',
    iconKey: 'briefcase',
    desc:
      'Partnering with in‑house teams on incorporations, restructurings, and cross‑border transactions.',
  },
  {
    id: '03',
    title: 'Deal Execution',
    iconKey: 'handshake',
    desc:
      'From term sheets to closing, we manage the legal workstream on complex M&A and financing deals.',
  },
  {
    id: '04',
    title: 'Compliance & IP',
    iconKey: 'shield-check',
    desc:
      'Designing compliance programs and protecting your trademarks, copyrights and trade secrets.',
  },
];

const FeaturesSection = () => {
  const [features, setFeatures] = useState(defaultFeatures);
  const [stats, setStats] = useState([
    {
      id: 'stat-01',
      title: 'Digital Platform Services',
      description:
        'Building scalable, high-performance digital ecosystems and B2B solutions.',
      imageUrl:
        'https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=1000',
      overlayType: 'blue',
    },
    {
      id: 'stat-02',
      title: 'Corporate Advisory',
      description:
        'Strategic legal and business guidance for market leaders and emerging startups.',
      imageUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1000',
      overlayType: 'purple',
    },
    {
      id: 'stat-03',
      title: 'International & Regional Firms',
      description:
        'Acting as local counsel and strategic partners to leading firms across key jurisdictions.',
      imageUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1000',
      overlayType: 'purple',
    },
  ]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [featuresRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/features`),
          axios.get(`${API_BASE_URL}/api/feature-stats`),
        ]);

        if (Array.isArray(featuresRes.data?.features)) {
          setFeatures(featuresRes.data.features);
        }

        if (Array.isArray(statsRes.data?.stats)) {
          setStats(statsRes.data.stats);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };

    fetchAll();
  }, []);

  const hasFeatureRows = features.length > 0;
  const hasStatRows = stats.length > 0;
  const hasBodyContent = hasFeatureRows || hasStatRows;

  return (
    <section
      className={`features-wrapper${!hasBodyContent ? ' features-wrapper--no-body' : ''}`}
    >
      <div className="container">
        <header className="features-section-heading text-center mb-5">
          <p className="features-subtitle">WHY CHOOSE US</p>
          <h2 className="features-title">Clear Advice. Practical Solutions.</h2>
        </header>

        <div className="row">
          {features.map((item) => {
            const iconName = normalizeServiceIconKey(item.iconKey);
            return (
              <div key={item.id} className="col-md-3 col-12">
                <div className="feature-card">
                  <DynamicIcon
                    name={iconName}
                    fallback={Scale}
                    size={32}
                    strokeWidth={1.5}
                    color="#6EACDA"
                    className="card-icon-legal"
                  />
                  <h4 className="card-title">{item.title}</h4>
                  <p className="card-text">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats / Why choose us cards */}
        <div style={{ display: stats.length > 0 ? '' : 'none' }} className="features-stats-row">
          {stats.map((stat) => {
            const overlayClass =
              stat.overlayType === 'blue'
                ? 'stat-overlay-blue'
                : 'stat-overlay-purple';
            const boxClass =
              stat.overlayType === 'blue'
                ? 'stat-box box-left'
                : 'stat-box box-right';

            const imageSrc =
              stat.imageUrl && !stat.imageUrl.startsWith('http')
                ? `${API_BASE_URL}${stat.imageUrl}`
                : stat.imageUrl;

            const boxStyle = imageSrc
              ? {
                  backgroundImage: `url(${imageSrc})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined;

            return (
              <div key={stat.id} className="features-stat-col">
                <div className={boxClass} style={boxStyle}>
                  <div className="stat-content">
                    <h5 className="stat-label">{stat.title}</h5>
                    <p className="stat-desc">{stat.description}</p>
                  </div>
                  <div className={overlayClass}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
